import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Footprints,
  Package,
  Users,
  UserCheck,
  LogOut,
  Search,
  Plus,
  Edit,
  Trash2,
  Upload,
  FileText,
  MessageSquare,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Send,
  Truck,
  Key,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  1: "bg-blue-100 text-blue-800",
  2: "bg-yellow-100 text-yellow-800",
  3: "bg-orange-100 text-orange-800",
  4: "bg-green-100 text-green-800",
  5: "bg-emerald-100 text-emerald-800",
};

const statusOptions = [
  { value: 1, label: "Order received by lab" },
  { value: 2, label: "In production" },
  { value: 3, label: "Order being packaged" },
  { value: 4, label: "Shipped/Ready to pick up" },
  { value: 5, label: "Clinic received order" },
];

const AdminSidebar = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const links = [
    { path: "/admin", icon: Package, label: "All Orders", exact: true },
    { path: "/admin/customers", icon: Users, label: "All Customers" },
    { path: "/admin/pending", icon: UserCheck, label: "Pending Approvals" },
    { path: "/admin/emails", icon: Mail, label: "Email History" },
    { path: "/admin/messages", icon: MessageSquare, label: "Messages" },
  ];

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 hidden md:flex flex-col">
      {/* Red accent bar at top */}
      <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
      <div className="p-6 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2">
          <Footprints className="w-7 h-7 text-[#0F766E]" />
          <span className="text-lg font-bold text-[#0F766E] font-['Manrope']">
            Admin Panel
          </span>
        </Link>
      </div>
      <div className="p-4 border-b border-slate-200 bg-[#DC2626]/5">
        <p className="text-xs text-[#DC2626] font-semibold uppercase tracking-wider">Administrator</p>
        <p className="text-sm text-slate-600 mt-0.5">Ortho Canada Lab</p>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {links.map((link) => {
          const isActive = link.exact
            ? location.pathname === link.path
            : location.pathname.startsWith(link.path);
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`admin-sidebar-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-[#DC2626] hover:bg-red-50"
          data-testid="admin-sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </aside>
  );
};

const MobileAdminHeader = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200">
      {/* Red accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
      <div className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Footprints className="w-6 h-6 text-[#0F766E]" />
          <span className="font-bold text-[#0F766E]">Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/admin">
            <Button variant={location.pathname === "/admin" ? "default" : "ghost"} size="sm" className={location.pathname === "/admin" ? "bg-[#0F766E]" : ""}>
              <Package className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/admin/customers">
            <Button variant={location.pathname === "/admin/customers" ? "default" : "ghost"} size="sm" className={location.pathname === "/admin/customers" ? "bg-[#0F766E]" : ""}>
              <Users className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/admin/pending">
            <Button variant={location.pathname === "/admin/pending" ? "default" : "ghost"} size="sm" className={location.pathname === "/admin/pending" ? "bg-[#0F766E]" : ""}>
              <UserCheck className="w-4 h-4" />
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 text-[#DC2626]" />
          </Button>
        </div>
      </div>
    </header>
  );
};

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', id: '', name: '' });
  const [emailType, setEmailType] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [newOrder, setNewOrder] = useState({ invoice_number: "", clinic_id: "", patient_name: "", item_description: "", cost: "" });
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState("");
  const [uploadData, setUploadData] = useState({ doc_type: "invoice", file: null });
  const [sendingEmail, setSendingEmail] = useState(false);
  const navigate = useNavigate();

  const downloadDocument = (docId, filename) => {
    console.log('Admin download clicked:', docId, filename);
    
    const token = localStorage.getItem('token');
    const downloadUrl = `${API}/documents/${docId}/direct-download?token=${encodeURIComponent(token)}`;
    
    fetch(downloadUrl)
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        console.log('Blob size:', blob.size);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download error:', error);
        toast.error('Failed to download document');
      });
  };

  const deleteDocument = async (docId, filename) => {
    setDeleteTarget({ type: 'document', id: docId, name: filename });
    setShowDeleteConfirm(true);
  };

  const handleDeleteOrder = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    setDeleteTarget({ type: 'order', id: orderId, name: order?.invoice_number || orderId });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget.type === 'document') {
        await axios.delete(`${API}/admin/documents/${deleteTarget.id}`);
        toast.success('Document deleted');
      } else if (deleteTarget.type === 'order') {
        await axios.delete(`${API}/admin/orders/${deleteTarget.id}`);
        toast.success('Order deleted');
      }
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Failed to delete ${deleteTarget.type}`);
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget({ type: '', id: '', name: '' });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/users`),
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data.filter((c) => c.status === "approved"));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!newOrder.invoice_number || !newOrder.clinic_id || !newOrder.patient_name || !newOrder.cost) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await axios.post(`${API}/admin/orders`, {
        invoice_number: newOrder.invoice_number,
        clinic_id: newOrder.clinic_id,
        patient_name: newOrder.patient_name,
        item_description: newOrder.item_description || "",
        cost: parseFloat(newOrder.cost),
      });
      toast.success("Order created successfully");
      setShowCreateDialog(false);
      setNewOrder({ invoice_number: "", clinic_id: "", patient_name: "", item_description: "", cost: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    }
  };

  const handleUpdateOrder = async () => {
    try {
      const updatePayload = {};
      if (editData.patient_name) updatePayload.patient_name = editData.patient_name;
      if (editData.item_description !== undefined) updatePayload.item_description = editData.item_description;
      if (editData.status) updatePayload.status = parseInt(editData.status);
      if (editData.cost) updatePayload.cost = parseFloat(editData.cost);
      if (editData.tracking_number !== undefined) updatePayload.tracking_number = editData.tracking_number;

      await axios.put(`${API}/admin/orders/${selectedOrder.id}`, updatePayload);
      toast.success("Order updated successfully");
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadData.file) {
      toast.error("Please select a file");
      return;
    }
    const formData = new FormData();
    formData.append("file", uploadData.file);
    formData.append("doc_type", uploadData.doc_type);

    try {
      await axios.post(`${API}/admin/orders/${selectedOrder.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setShowUploadDialog(false);
      setUploadData({ doc_type: "invoice", file: null });
      fetchData();
    } catch (error) {
      toast.error("Failed to upload document");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }
    try {
      await axios.post(`${API}/admin/orders/${selectedOrder.id}/notes`, { content: newNote });
      toast.success("Note added");
      setShowNoteDialog(false);
      setNewNote("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditData({
      patient_name: order.patient_name,
      item_description: order.item_description || "",
      status: order.status.toString(),
      cost: order.cost.toString(),
      tracking_number: order.tracking_number || "",
    });
    setShowEditDialog(true);
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map((o) => o.id));
    }
  };

  const openEmailDialog = (type) => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order");
      return;
    }
    setEmailType(type);
    setShowEmailDialog(true);
  };

  const handleSendBulkEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/send-bulk-email`, {
        order_ids: selectedOrders,
        email_type: emailType,
      });
      toast.success(`Email sent to ${response.data.emails_sent} clinic(s)`);
      setShowEmailDialog(false);
      setSelectedOrders([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const getSelectedOrdersPreview = () => {
    return orders.filter((o) => selectedOrders.includes(o.id));
  };

  const filteredOrders = orders.filter((order) => {
    const term = searchTerm.toLowerCase();
    return (
      order.invoice_number.toLowerCase().includes(term) ||
      order.patient_name.toLowerCase().includes(term) ||
      order.clinic_name.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">All Orders</h1>
          <p className="text-slate-600">Manage all orthotic orders</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="admin-orders-search"
            />
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F766E] hover:bg-[#115E59]" data-testid="admin-create-order-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Order
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Order</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={newOrder.invoice_number}
                    onChange={(e) => setNewOrder({ ...newOrder, invoice_number: e.target.value })}
                    placeholder="INV-001"
                    data-testid="new-order-invoice-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clinic</Label>
                  <Select
                    value={newOrder.clinic_id}
                    onValueChange={(val) => setNewOrder({ ...newOrder, clinic_id: val })}
                  >
                    <SelectTrigger data-testid="new-order-clinic-select">
                      <SelectValue placeholder="Select clinic" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.clinic_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Patient Name</Label>
                  <Input
                    value={newOrder.patient_name}
                    onChange={(e) => setNewOrder({ ...newOrder, patient_name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="new-order-patient-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Item Description</Label>
                  <Input
                    value={newOrder.item_description}
                    onChange={(e) => setNewOrder({ ...newOrder, item_description: e.target.value })}
                    placeholder="Custom Foot Orthotics, Sport Insoles, etc."
                    data-testid="new-order-item-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost ($)</Label>
                  <Input
                    type="number"
                    value={newOrder.cost}
                    onChange={(e) => setNewOrder({ ...newOrder, cost: e.target.value })}
                    placeholder="150.00"
                    data-testid="new-order-cost-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button className="bg-[#0F766E] hover:bg-[#115E59]" onClick={handleCreateOrder} data-testid="new-order-submit-btn">
                  Create Order
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Email Alert Buttons */}
      {selectedOrders.length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-[#F0FDFA] border border-[#0F766E]/20 rounded-lg">
          <span className="text-sm font-medium text-[#0F766E]">
            {selectedOrders.length} order(s) selected
          </span>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => openEmailDialog("received")}
            className="border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E]/10"
            data-testid="send-received-email-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send "Order Received" Email
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openEmailDialog("shipped")}
            className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10"
            data-testid="send-shipped-email-btn"
          >
            <Truck className="w-4 h-4 mr-2" />
            Send "Shipped/Ready" Email
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedOrders([])}
            className="text-slate-500"
          >
            Clear Selection
          </Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-orders"
                  />
                </TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead>Clinic</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Item Description</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Docs</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    No orders found
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order.id} className={selectedOrders.includes(order.id) ? "bg-[#F0FDFA]" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onCheckedChange={() => toggleOrderSelection(order.id)}
                        data-testid={`select-order-${order.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="text-[#0F766E] font-medium hover:underline"
                      >
                        {order.invoice_number}
                      </button>
                    </TableCell>
                    <TableCell>{order.clinic_name}</TableCell>
                    <TableCell className="font-medium">{order.patient_name}</TableCell>
                    <TableCell className="text-slate-600 max-w-[200px] truncate" title={order.item_description || "-"}>
                      {order.item_description || "-"}
                    </TableCell>
                    <TableCell>{new Date(order.date_received).toLocaleDateString("en-CA")}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[order.status]}>{order.status_label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">${order.cost.toFixed(2)}</TableCell>
                    <TableCell>
                      {order.documents?.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {order.documents.map((doc) => {
                            const docTypeLabels = {
                              'invoice': 'Invoice',
                              'proof_of_manufacturing': 'Proof of Mfg',
                              'packing_slip': 'Packing Slip'
                            };
                            const docTypeLabel = docTypeLabels[doc.doc_type] || doc.doc_type;
                            return (
                              <div key={doc.id} className="flex items-center gap-1">
                                <button
                                  onClick={() => downloadDocument(doc.id, doc.filename)}
                                  className="p-1 hover:bg-slate-100 rounded"
                                  title={`${docTypeLabel}: ${doc.filename}`}
                                >
                                  <FileText className="w-4 h-4 text-[#0F766E]" />
                                </button>
                                <button
                                  onClick={() => deleteDocument(doc.id, doc.filename)}
                                  className="p-1 hover:bg-red-100 rounded"
                                  title={`Delete ${doc.filename}`}
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(order)}
                          data-testid={`edit-order-${order.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowUploadDialog(true);
                          }}
                          data-testid={`upload-doc-${order.id}`}
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowNoteDialog(true);
                          }}
                          data-testid={`add-note-${order.id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteOrder(order.id)}
                          className="text-[#DC2626] hover:text-[#DC2626] hover:bg-red-50"
                          data-testid={`delete-order-${order.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Patient Name</Label>
              <Input
                value={editData.patient_name || ""}
                onChange={(e) => setEditData({ ...editData, patient_name: e.target.value })}
                data-testid="edit-order-patient"
              />
            </div>
            <div className="space-y-2">
              <Label>Item Description</Label>
              <Input
                value={editData.item_description || ""}
                onChange={(e) => setEditData({ ...editData, item_description: e.target.value })}
                placeholder="Custom Foot Orthotics, Sport Insoles, etc."
                data-testid="edit-order-item-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editData.status}
                onValueChange={(val) => setEditData({ ...editData, status: val })}
              >
                <SelectTrigger data-testid="edit-order-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value.toString()}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cost ($)</Label>
              <Input
                type="number"
                value={editData.cost || ""}
                onChange={(e) => setEditData({ ...editData, cost: e.target.value })}
                data-testid="edit-order-cost"
              />
            </div>
            <div className="space-y-2">
              <Label>Tracking Number</Label>
              <Input
                value={editData.tracking_number || ""}
                onChange={(e) => setEditData({ ...editData, tracking_number: e.target.value })}
                placeholder="Enter tracking number"
                data-testid="edit-order-tracking"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button className="bg-[#0F766E] hover:bg-[#115E59]" onClick={handleUpdateOrder} data-testid="edit-order-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select
                value={uploadData.doc_type}
                onValueChange={(val) => setUploadData({ ...uploadData, doc_type: val })}
              >
                <SelectTrigger data-testid="upload-doc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="proof_of_manufacturing">Proof of Manufacturing</SelectItem>
                  <SelectItem value="packing_slip">Packing Slip</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File (PDF)</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                data-testid="upload-doc-file"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button className="bg-[#0F766E] hover:bg-[#115E59]" onClick={handleUploadDocument} data-testid="upload-doc-submit">
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Note Content</Label>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter note..."
                rows={4}
                data-testid="add-note-content"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button className="bg-[#0F766E] hover:bg-[#115E59]" onClick={handleAddNote} data-testid="add-note-submit">
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Confirmation Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {emailType === "received" ? (
                <>
                  <Mail className="w-5 h-5 text-[#0F766E]" />
                  Send "Order Received" Email
                </>
              ) : (
                <>
                  <Truck className="w-5 h-5 text-[#DC2626]" />
                  Send "Shipped/Ready" Email
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              This will send ONE email per clinic listing the selected orders.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-3">
              <strong>{selectedOrders.length}</strong> order(s) selected:
            </p>
            <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-3 bg-slate-50">
              {getSelectedOrdersPreview().map((order) => (
                <div key={order.id} className="flex justify-between text-sm">
                  <span className="font-medium">{order.invoice_number}</span>
                  <span className="text-slate-500">{order.clinic_name} - {order.patient_name}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {emailType === "received" 
                ? "Email will confirm that the orders have been received by the lab."
                : "Email will notify that the orders are shipped or ready for pick up."}
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              className={emailType === "received" ? "bg-[#0F766E] hover:bg-[#115E59]" : "bg-[#DC2626] hover:bg-[#B91C1C]"}
              onClick={handleSendBulkEmail}
              disabled={sendingEmail}
              data-testid="confirm-send-email-btn"
            >
              {sendingEmail ? (
                <span className="flex items-center gap-2">
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                  Sending...
                </span>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteTarget.type}?
              <br />
              <strong>{deleteTarget.name}</strong>
              {deleteTarget.type === 'order' && (
                <p className="mt-2 text-red-500">This will also delete all associated documents.</p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AllCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editData, setEditData] = useState({});
  const [resetData, setResetData] = useState({ username: "", temporary_password: "" });
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setEditData({
      first_name: customer.first_name,
      last_name: customer.last_name,
      clinic_name: customer.clinic_name,
      clinic_address: customer.clinic_address,
      email: customer.email,
      phone: customer.phone,
    });
    setShowEditDialog(true);
  };

  const handleUpdateCustomer = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedCustomer.id}`, editData);
      toast.success("Customer updated successfully");
      setShowEditDialog(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer");
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedCustomer.id}`);
      toast.success("Customer deleted successfully");
      setShowDeleteDialog(false);
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete customer");
    }
  };

  const openResetDialog = (customer) => {
    setSelectedCustomer(customer);
    setResetData({
      username: customer.username || customer.email.split("@")[0].toLowerCase(),
      temporary_password: `Reset${Math.random().toString(36).slice(-8)}!`,
    });
    setShowResetDialog(true);
  };

  const handleResetCredentials = async () => {
    if (!resetData.username || !resetData.temporary_password) {
      toast.error("Please fill all fields");
      return;
    }
    setResetting(true);
    try {
      const response = await axios.post(`${API}/admin/users/${selectedCustomer.id}/reset-credentials`, resetData);
      if (response.data.email_sent) {
        toast.success(`Credentials reset! New credentials sent to ${response.data.email}`);
      } else {
        toast.success("Credentials reset! But email could not be sent - please share credentials manually.");
      }
      setShowResetDialog(false);
      setResetData({ username: "", temporary_password: "" });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset credentials");
    } finally {
      setResetting(false);
    }
  };

  const filteredCustomers = customers.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      c.clinic_name.toLowerCase().includes(term) ||
      c.email.toLowerCase().includes(term) ||
      c.first_name.toLowerCase().includes(term) ||
      c.last_name.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">All Customers</h1>
          <p className="text-slate-600">View and manage registered clinics</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="admin-customers-search"
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Clinic Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No customers found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium">{customer.clinic_name}</TableCell>
                    <TableCell>{customer.first_name} {customer.last_name}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          customer.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : customer.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        {customer.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(customer.created_at).toLocaleDateString("en-CA")}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(customer)}
                          title="Edit customer"
                          data-testid={`edit-customer-${customer.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {customer.status === "approved" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetDialog(customer)}
                            title="Reset credentials"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            data-testid={`reset-customer-${customer.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowDeleteDialog(true);
                          }}
                          title="Delete customer"
                          className="text-[#DC2626] hover:text-[#DC2626] hover:bg-red-50"
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>Update clinic information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editData.first_name || ""}
                  onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                  data-testid="edit-customer-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editData.last_name || ""}
                  onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                  data-testid="edit-customer-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Clinic Name</Label>
              <Input
                value={editData.clinic_name || ""}
                onChange={(e) => setEditData({ ...editData, clinic_name: e.target.value })}
                data-testid="edit-customer-clinic-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Clinic Address</Label>
              <Input
                value={editData.clinic_address || ""}
                onChange={(e) => setEditData({ ...editData, clinic_address: e.target.value })}
                data-testid="edit-customer-clinic-address"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editData.email || ""}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                data-testid="edit-customer-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editData.phone || ""}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                data-testid="edit-customer-phone"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button className="bg-[#0F766E] hover:bg-[#115E59]" onClick={handleUpdateCustomer} data-testid="edit-customer-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#DC2626]">Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this clinic? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium">{selectedCustomer.clinic_name}</p>
                <p className="text-sm text-slate-600">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                <p className="text-sm text-slate-500">{selectedCustomer.email}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              className="bg-[#DC2626] hover:bg-[#B91C1C]" 
              onClick={handleDeleteCustomer}
              data-testid="confirm-delete-customer-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Credentials Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Reset Credentials
            </DialogTitle>
            <DialogDescription>
              Set new login credentials for this clinic. An email with the new credentials will be sent.
            </DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium">{selectedCustomer.clinic_name}</p>
                <p className="text-sm text-slate-600">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                <p className="text-sm text-slate-500">{selectedCustomer.email}</p>
              </div>
              <div className="space-y-2">
                <Label>New Username</Label>
                <Input
                  value={resetData.username}
                  onChange={(e) => setResetData({ ...resetData, username: e.target.value })}
                  data-testid="reset-username"
                />
              </div>
              <div className="space-y-2">
                <Label>New Temporary Password</Label>
                <Input
                  value={resetData.temporary_password}
                  onChange={(e) => setResetData({ ...resetData, temporary_password: e.target.value })}
                  data-testid="reset-password"
                />
                <p className="text-xs text-slate-500">User will be asked to change this on first login.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              className="bg-amber-600 hover:bg-amber-700" 
              onClick={handleResetCredentials}
              disabled={resetting}
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <span className="flex items-center gap-2">
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                  Resetting...
                </span>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Reset & Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PendingApprovals = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvalData, setApprovalData] = useState({ username: "", temporary_password: "" });

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/pending`);
      setPendingUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch pending users");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approvalData.username || !approvalData.temporary_password) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/users/${selectedUser.id}/approve`, approvalData);
      if (response.data.email_sent) {
        toast.success(`User approved! Credentials sent to ${response.data.email}`);
      } else {
        toast.success("User approved! But email could not be sent - please share credentials manually.");
      }
      setShowApproveDialog(false);
      setApprovalData({ username: "", temporary_password: "" });
      fetchPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm("Are you sure you want to reject this registration?")) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`);
      toast.success("User rejected");
      fetchPending();
    } catch (error) {
      toast.error("Failed to reject user");
    }
  };

  const openApproveDialog = (user) => {
    setSelectedUser(user);
    setApprovalData({
      username: user.email.split("@")[0].toLowerCase(),
      temporary_password: `Temp${Math.random().toString(36).slice(-8)}!`,
    });
    setShowApproveDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Pending Approvals</h1>
        <p className="text-slate-600">Review and approve clinic registrations</p>
      </div>

      {pendingUsers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No pending registrations</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {pendingUsers.map((user) => (
            <Card key={user.id}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="space-y-1">
                    <h3 className="font-semibold text-lg">{user.clinic_name}</h3>
                    <p className="text-slate-600">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-sm text-slate-500">{user.email} | {user.phone}</p>
                    <p className="text-sm text-slate-500">{user.clinic_address}</p>
                    <p className="text-xs text-slate-400">
                      Registered: {new Date(user.created_at).toLocaleString("en-CA")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openApproveDialog(user)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-user-${user.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleReject(user.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`reject-user-${user.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Registration</DialogTitle>
            <DialogDescription>
              Set credentials for {selectedUser?.clinic_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                value={approvalData.username}
                onChange={(e) => setApprovalData({ ...approvalData, username: e.target.value })}
                data-testid="approve-username"
              />
            </div>
            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <Input
                value={approvalData.temporary_password}
                onChange={(e) => setApprovalData({ ...approvalData, temporary_password: e.target.value })}
                data-testid="approve-password"
              />
              <p className="text-xs text-slate-500">User will be asked to change this on first login.</p>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button className="bg-[#0F766E] hover:bg-[#115E59]" onClick={handleApprove} data-testid="approve-submit-btn">
              Approve & Send Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Messages = () => {
  const [orderMessages, setOrderMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/order-messages`);
      // Sort order messages by created_at descending (newest first)
      const sortedOrder = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setOrderMessages(sortedOrder);
    } catch (error) {
      toast.error("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (messageId) => {
    if (!replyText.trim()) {
      toast.error("Please enter a reply");
      return;
    }
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/order-messages/${messageId}/reply`, { reply: replyText });
      toast.success("Reply sent");
      setReplyingTo(null);
      setReplyText("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const handleDeleteClick = (msg) => {
    setMessageToDelete(msg);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/order-messages/${messageToDelete.id}`);
      toast.success("Message deleted");
      setShowDeleteDialog(false);
      setMessageToDelete(null);
      fetchMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  const unreadCount = orderMessages.filter(m => !m.read && !m.reply).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Messages</h1>
          <p className="text-slate-600">Customer messages and order inquiries</p>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-[#0F766E] text-white">
            {unreadCount} unread
          </Badge>
        )}
      </div>

      {orderMessages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No messages yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orderMessages.map((msg) => (
            <Card key={msg.id} className={!msg.read && !msg.reply ? "border-l-4 border-l-[#0F766E]" : ""}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{msg.clinic_name}</h3>
                      {!msg.read && !msg.reply && (
                        <Badge className="bg-[#0F766E] text-white text-xs">New</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      Order: <span className="font-mono text-[#0F766E]">{msg.invoice_number}</span>
                      {msg.patient_name && <span> • Patient: {msg.patient_name}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{msg.created_at}</span>
                    <button
                      onClick={() => handleDeleteClick(msg)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Delete message"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Customer Message */}
                <div className="p-3 bg-blue-50 rounded-lg mb-3">
                  <p className="text-sm font-medium text-blue-700 mb-1">Customer Message:</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                </div>

                {/* Reply Section */}
                {msg.reply ? (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-700 mb-1">Your Reply ({msg.reply_at}):</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{msg.reply}</p>
                  </div>
                ) : replyingTo === msg.id ? (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => sendReply(msg.id)}
                        disabled={sendingReply}
                        className="bg-[#0F766E] hover:bg-[#0D635C]"
                      >
                        {sendingReply ? "Sending..." : "Send Reply"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReplyingTo(msg.id)}
                    className="text-[#0F766E] border-[#0F766E] hover:bg-[#0F766E] hover:text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Message</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this message?
              {messageToDelete && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg text-left">
                  <p className="text-sm">
                    <strong>From:</strong> {messageToDelete.clinic_name}
                  </p>
                  <p className="text-sm">
                    <strong>Order:</strong> {messageToDelete.invoice_number}
                  </p>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                    "{messageToDelete.message}"
                  </p>
                </div>
              )}
              <p className="mt-3 text-red-500 text-sm">This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmailHistory = () => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedLogs, setExpandedLogs] = useState({});

  useEffect(() => {
    fetchEmailLogs();
  }, []);

  const fetchEmailLogs = async (search = "", fromDate = "", toDate = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (fromDate) params.append("date_from", fromDate);
      if (toDate) params.append("date_to", toDate);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/admin/email-logs${queryString}`);
      setEmailLogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch email history");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEmailLogs(searchTerm, dateFrom, dateTo);
  };

  const handleClear = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    fetchEmailLogs();
  };

  const toggleExpand = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getLogIcon = (log) => {
    if (log.email_type === "approval") {
      if (log.action === "approved") {
        return <UserCheck className="w-5 h-5 text-green-600" />;
      } else {
        return <XCircle className="w-5 h-5 text-[#DC2626]" />;
      }
    } else if (log.email_type === "credential_reset") {
      return <Key className="w-5 h-5 text-amber-600" />;
    } else if (log.email_type === "received") {
      return <Package className="w-5 h-5 text-[#0F766E]" />;
    } else {
      return <Truck className="w-5 h-5 text-[#DC2626]" />;
    }
  };

  const getLogBgColor = (log) => {
    if (log.email_type === "approval") {
      return log.action === "approved" ? "bg-green-50" : "bg-red-50";
    } else if (log.email_type === "credential_reset") {
      return "bg-amber-50";
    } else if (log.email_type === "received") {
      return "bg-[#F0FDFA]";
    } else {
      return "bg-red-50";
    }
  };

  const getLogTitle = (log) => {
    if (log.email_type === "approval") {
      if (log.action === "approved") {
        return "Clinic Approved";
      } else {
        return "Clinic Rejected";
      }
    } else if (log.email_type === "credential_reset") {
      return "Credentials Reset";
    } else if (log.email_type === "received") {
      return "Orders Received Notification";
    } else {
      return "Shipped/Ready Notification";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Email History</h1>
        <p className="text-slate-600">Track all sent email notifications and approval actions</p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="space-y-3">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by patient name, invoice #, clinic..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="email-history-search"
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex flex-col">
              <label className="text-xs text-slate-500 mb-1">From Date</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px]"
                data-testid="email-history-date-from"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-slate-500 mb-1">To Date</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px]"
                data-testid="email-history-date-to"
              />
            </div>
          </div>
          <Button type="submit" className="bg-[#0F766E] hover:bg-[#0D635C]">
            Search
          </Button>
          {(searchTerm || dateFrom || dateTo) && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </div>
      </form>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
        </div>
      ) : emailLogs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No emails sent yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {emailLogs.map((log) => (
            <Card key={log.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getLogBgColor(log)}`}>
                      {getLogIcon(log)}
                    </div>
                    <div>
                      <h3 className="font-semibold">
                        {getLogTitle(log)}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {log.sent_at}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {log.email_type === "approval" ? (
                      <Badge className={log.action === "approved" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                        {log.action === "approved" ? "Approved" : "Rejected"}
                      </Badge>
                    ) : log.email_type === "credential_reset" ? (
                      <Badge className="bg-amber-100 text-amber-800">
                        Reset
                      </Badge>
                    ) : (
                      <>
                        <Badge className={log.emails_sent?.length > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                          {log.emails_sent?.length || 0} sent
                        </Badge>
                        {log.emails_failed?.length > 0 && (
                          <Badge className="bg-red-100 text-red-800 ml-2">
                            {log.emails_failed.length} failed
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* For approval/rejection/reset logs */}
                {(log.email_type === "approval" || log.email_type === "credential_reset") && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Clinic:</p>
                    <div className={`rounded-lg p-3 ${
                      log.email_type === "credential_reset" 
                        ? "bg-amber-50 border border-amber-200"
                        : log.action === "approved" 
                          ? "bg-green-50 border border-green-200" 
                          : "bg-red-50 border border-red-200"
                    }`}>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {log.email_type === "credential_reset" ? (
                            <Key className="w-4 h-4 text-amber-600" />
                          ) : log.action === "approved" ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="font-medium">{log.clinic_name}</span>
                        </div>
                        {log.emails_sent?.[0]?.to && (
                          <span className="text-slate-500">{log.emails_sent[0].to}</span>
                        )}
                        {log.rejection_info?.email && (
                          <span className="text-slate-500">{log.rejection_info.email}</span>
                        )}
                      </div>
                      {log.email_type === "credential_reset" && (
                        <p className="text-xs text-amber-700 mt-1">
                          New credentials email {log.emails_sent?.[0]?.success ? "sent successfully" : "failed to send"}
                        </p>
                      )}
                      {log.email_type === "approval" && log.action === "approved" && log.emails_sent?.[0] && (
                        <p className="text-xs text-green-700 mt-1">
                          Credentials email {log.emails_sent[0].success ? "sent successfully" : "failed to send"}
                        </p>
                      )}
                      {log.email_type === "approval" && log.action === "rejected" && (
                        <p className="text-xs text-red-700 mt-1">
                          Registration rejected - no email sent
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* For order notification logs */}
                {(log.email_type === "received" || log.email_type === "shipped") && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-slate-700">Recipients & Orders:</p>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                      {log.emails_sent?.map((email, idx) => (
                        <div key={idx} className="border-b border-slate-200 pb-3 last:border-0 last:pb-0">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="font-medium">{email.clinic_name}</span>
                              <span className="text-slate-500">({email.to})</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpand(`${log.id}-${idx}`)}
                              className="text-xs"
                            >
                              {expandedLogs[`${log.id}-${idx}`] ? (
                                <>Hide Orders <ChevronUp className="w-3 h-3 ml-1" /></>
                              ) : (
                                <>{email.order_count || email.orders?.length || 0} order(s) <ChevronDown className="w-3 h-3 ml-1" /></>
                              )}
                            </Button>
                          </div>
                          {/* Order Details - Expandable */}
                          {expandedLogs[`${log.id}-${idx}`] && email.orders && (
                            <div className="ml-6 mt-2 space-y-1">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-slate-500">
                                    <th className="pb-1 font-medium">Invoice #</th>
                                    <th className="pb-1 font-medium">Patient Name</th>
                                    {log.email_type === "shipped" && <th className="pb-1 font-medium">Tracking</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {email.orders.map((order, orderIdx) => (
                                    <tr key={orderIdx} className="text-slate-700">
                                      <td className="py-1 font-mono text-[#0F766E]">{order.invoice_number}</td>
                                      <td className="py-1">{order.patient_name}</td>
                                      {log.email_type === "shipped" && (
                                        <td className="py-1 text-slate-500">{order.tracking_number || 'N/A'}</td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                      {log.emails_failed?.map((email, idx) => (
                        <div key={`failed-${idx}`} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-red-600" />
                            <span className="font-medium">{email.clinic_name}</span>
                            <span className="text-slate-500">({email.to})</span>
                          </div>
                          <span className="text-red-500">Failed</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      {log.order_ids?.length} order(s) included • {log.sent_at}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar />
      <MobileAdminHeader />
      <main className="md:ml-64 p-6 md:p-8" data-testid="admin-dashboard">
        <Routes>
          <Route index element={<AllOrders />} />
          <Route path="customers" element={<AllCustomers />} />
          <Route path="pending" element={<PendingApprovals />} />
          <Route path="emails" element={<EmailHistory />} />
          <Route path="messages" element={<Messages />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
